import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import moment from 'moment'
import { isNil, max, range, round } from 'lodash'
import { getMedianZoomBounds, getObservedSerie, getPredSeries, getRainSeries } from '../../utils/IAEauPredSeriesUtil'
import { getMeasureJplus } from '../../utils/IAEauUtils'
import { getFullDate } from '../../../utils/DateUtil'
import { hasValue } from '../../../utils/NumberUtil'
import EChartV6 from '../../../components/echart/EChartV6'

const LEGEND_HEIGHT = 50
const GRID_HEIGHT = 220
const GRID_TITLE_HEIGHT = 50
const BOTTOM_HEIGHT = 80
const GRID_LEFT = 110
const GRID_RIGHT = 110
const RAIN_UNIT = 'mm'
const RAIN_ZOOM_ID = 'rain-zoom'

const getGridTop = index => LEGEND_HEIGHT + GRID_TITLE_HEIGHT + index * (GRID_HEIGHT + GRID_TITLE_HEIGHT)

const hasRain = ({ rainMeasures, rainPredMeasures }) => !!(rainMeasures?.length || rainPredMeasures?.length)

const getSimulationLabel = (simulationDate, jPlus) => {
    if (!isNil(jPlus)) {
        return `J+${jPlus}`
    }
    return isNil(simulationDate) ? i18n.noResults : `${i18n.simulationDate} : ${getFullDate(simulationDate)}`
}

const getGridTitle = ({ model, simulationDate, predMeasures }, withoutHack, jPlus) => {
    const station = [model.stationCode && `[${model.stationCode}]`, model.stationName].filter(s => !!s).join(' ')
    const simulation = getSimulationLabel(simulationDate, jPlus)
    const gap = predMeasures.find(m => hasValue(m.gap))?.gap
    const gapLabel = withoutHack && hasValue(gap) && `(gap = ${round(gap, 3)})`
    return [model.name, station, simulation, gapLabel].filter(s => !!s).join(' - ')
}

// Horizon du point survolé par rapport à la date de simulation de la grid, au format du dashboard SIEau
const getJplus = (date, simulationDate) => !isNil(simulationDate) && date >= simulationDate ? getMeasureJplus({ horizon: round((date - simulationDate) / 3600000) }) : ''

const formatTooltipRow = ({ marker, seriesName, seriesType, axisIndex, value, data }, units, jPlus) => {
    if (data?.rainStats) {
        const { median, q1, q3, min, max: maxValue } = data.rainStats
        return [`${marker} ${seriesName} : ${round(median, 1)} ${RAIN_UNIT} (Q1-Q3 : ${round(q1, 1)} – ${round(q3, 1)}, min-max : ${round(min, 1)} – ${round(maxValue, 1)})`]
    }
    const unit = seriesType === 'bar' ? RAIN_UNIT : (units[axisIndex] || '')
    if (data?.range) {
        const [lo, hi] = data.range
        return hasValue(lo) && hasValue(hi) ? [`${marker} ${seriesName} ${jPlus} : ${round(lo, 3)} – ${round(hi, 3)} ${unit}`] : []
    }
    const predJplus = data?.isPred ? ` ${jPlus}` : ''
    return hasValue(value[1]) ? [`${marker} ${seriesName}${predJplus} : ${round(value[1], 3)} ${unit}`] : []
}

const getYDataZoom = (yAxisIndex, gridIndex, position, id) => ({
    id,
    type: 'slider',
    yAxisIndex,
    orient: 'vertical',
    [position]: 15,
    width: 15,
    top: getGridTop(gridIndex),
    height: GRID_HEIGHT,
    filterMode: 'none',
    showDataShadow: false,
    labelFormatter: value => round(value, 2),
})

const IAEauModelsResultsChart = ({
    results = [],
    minDate,
    maxDate,
    getDataType = () => ({}),
    withoutHack = false,
    medianZoom = false,
    jPlus,
}) => {
    const chartRef = useRef()
    const hoveredGridRef = useRef()
    const nbGridsRef = useRef(0)
    nbGridsRef.current = results.length

    const option = useMemo(() => {
        const lastPredDate = max(results.flatMap(r => [...r.predMeasures.map(m => m.date), ...(r.rainPredMeasures ?? []).map(([date]) => date)]))
        const xMax = max([maxDate ?? moment().valueOf(), lastPredDate])
        const gridIndexes = results.map((_, idx) => idx)
        const units = results.map(({ model }) => getDataType(model).unit)
        const simulationDates = results.map(r => r.simulationDate)
        const nbGrids = results.length

        const grids = results.map((_, idx) => ({
            top: getGridTop(idx),
            height: GRID_HEIGHT,
            left: GRID_LEFT,
            right: GRID_RIGHT,
        }))
        const titles = results.map((result, idx) => ({
            text: getGridTitle(result, withoutHack, jPlus),
            top: getGridTop(idx) - 35,
            left: GRID_LEFT,
            textStyle: { fontSize: 14 },
        }))
        const xAxis = results.map((_, idx) => ({
            type: 'time',
            gridIndex: idx,
            min: minDate,
            max: xMax,
            splitLine: { show: true },
            axisLabel: {
                show: idx === nbGrids - 1,
                formatter: value => moment(value).format('DD/MM/YYYY'),
            },
        }))
        // Axes de gauche (courbes) aux index 0..n-1, axes de pluie (droite, inversés) aux index n..2n-1
        const yAxis = results.map((result, idx) => {
            const { label, unit } = getDataType(result.model)
            return {
                type: 'value',
                gridIndex: idx,
                scale: true,
                ...(medianZoom ? getMedianZoomBounds(result, minDate, xMax) : {}),
                name: [label, unit && `[${unit}]`].filter(s => !!s).join(' '),
                nameLocation: 'center',
                nameGap: 55,
                axisLine: { show: true },
                axisTick: { show: true },
                axisLabel: { formatter: value => round(value, 2) },
            }
        })
        const rainYAxis = results.map((result, idx) => ({
            type: 'value',
            gridIndex: idx,
            position: 'right',
            inverse: true,
            show: hasRain(result),
            min: 0,
            name: `${i18n.rain} [${RAIN_UNIT}]`,
            nameLocation: 'center',
            nameGap: 45,
            splitLine: { show: false },
            axisLine: { show: true },
            axisTick: { show: true },
            axisLabel: { formatter: value => round(value, 1) },
        }))
        const series = results.flatMap((result, idx) => [
            getObservedSerie({ measures: result.measures, name: getDataType(result.model).label || result.model.stationName || i18n.measures, gridIndex: idx, simulationDate: result.simulationDate }),
            ...getPredSeries({ predMeasures: result.predMeasures, model: result.model, gridIndex: idx }),
            ...getRainSeries({ rainMeasures: result.rainMeasures, rainPredMeasures: result.rainPredMeasures, gridIndex: idx, rainAxisIndex: nbGrids + idx }),
        ])
        const yDataZooms = results.flatMap((result, idx) => [
            getYDataZoom(idx, idx, 'left', `curve-zoom-${idx}`),
            ...(hasRain(result) ? [getYDataZoom(nbGrids + idx, idx, 'right', `${RAIN_ZOOM_ID}-${idx}`)] : []),
        ])

        // N'affiche que les séries de la grid survolée (le curseur vertical reste partagé entre les grids)
        const formatTooltip = params => {
            const gridParams = isNil(hoveredGridRef.current) ? params : params.filter(p => p.axisIndex === hoveredGridRef.current)
            if (!gridParams.length) {
                return ''
            }
            const date = gridParams[0].value[0]
            const jPlusLabel = isNil(jPlus) ? getJplus(date, simulationDates[gridParams[0].axisIndex]) : `(J+${jPlus})`
            const header = `${moment(date).format('DD/MM/YYYY HH:mm')} ${jPlusLabel}`
            const rows = gridParams
                .filter(({ data }) => !data?.isBandBase)
                .flatMap(param => formatTooltipRow(param, units, jPlusLabel))
            return [header, ...rows].join('<br/>')
        }

        return {
            animation: false,
            legend: { type: 'scroll', top: 10, left: GRID_LEFT, right: GRID_RIGHT },
            title: titles,
            grid: grids,
            xAxis,
            yAxis: [...yAxis, ...rainYAxis],
            series,
            axisPointer: { link: [{ xAxisIndex: 'all' }] },
            tooltip: {
                trigger: 'axis',
                formatter: formatTooltip,
            },
            dataZoom: [
                { type: 'inside', xAxisIndex: gridIndexes, filterMode: 'none' },
                { type: 'slider', xAxisIndex: gridIndexes, bottom: 15, filterMode: 'none' },
                ...yDataZooms,
            ],
        }
    }, [results, minDate, maxDate, getDataType, withoutHack, medianZoom, jPlus])

    const onChartReady = chart => {
        chartRef.current = chart
        chart.getZr().on('mousemove', e => {
            const gridIndex = range(nbGridsRef.current).find(idx => chart.containPixel({ gridIndex: idx }, [e.offsetX, e.offsetY]))
            hoveredGridRef.current = gridIndex
        })
    }

    // Le zoom de la pluie reste ancré sur 0 (haut de l'axe inversé) : seule la borne max se déplace
    const onDataZoom = e => {
        const zoom = e.batch?.[0] ?? e
        if (zoom.dataZoomId?.startsWith(RAIN_ZOOM_ID) && zoom.start > 0) {
            chartRef.current?.dispatchAction({ type: 'dataZoom', dataZoomId: zoom.dataZoomId, start: 0, end: zoom.end })
        }
    }

    const height = getGridTop(results.length - 1) + GRID_HEIGHT + BOTTOM_HEIGHT

    return (
        <EChartV6
            option={option}
            withToolbox={false}
            onChartReady={onChartReady}
            onEvents={{ datazoom: onDataZoom }}
            style={{ height }}
        />
    )
}

IAEauModelsResultsChart.propTypes = {
    results: PropTypes.arrayOf(PropTypes.shape({
        model: PropTypes.shape({}),
        simulationDate: PropTypes.number,
        predMeasures: PropTypes.arrayOf(PropTypes.shape({})),
        measures: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
        rainMeasures: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
        rainPredMeasures: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))),
    })),
    minDate: PropTypes.number,
    maxDate: PropTypes.number,
    getDataType: PropTypes.func,
    withoutHack: PropTypes.bool,
    medianZoom: PropTypes.bool,
    jPlus: PropTypes.number,
}

export default IAEauModelsResultsChart
